import View from 'ol/View';
import OLMap from 'ol/Map';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { ScaleLine, defaults as defaultControls } from 'ol/control';
import type Map from 'ol/Map';
import { extend } from 'ol/extent';
import type { Extent } from 'ol/extent';

import {
	CNET_CONSTRUCTOR_VECTOR_LAYER_STYLE,
	MEASURE_VECTOR_LAYER_STYLE,
} from '@/components/map/features/vectorLayerStyles';
import type { MapFeature } from '@/components/map/features/MapFeatures';
import MapHelpers from '@/components/map/utils/MapHelpers';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type { MapMode } from '@/components/map/types/mapFeatureTypes';
import {
	asTypedMapFeature,
	isTypedMapFeatureOfType,
} from '@/components/map/utils/mapFeatureGuards';
import { FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';


export type InitMapResult = {
	olMap: Map;
	vectorLayer: VectorLayer<VectorSource>;
	sourceCCM: VectorSource;
	tileLayer: TileLayer<OSM>;
	measureSource: VectorSource;
};


const getControls = () => defaultControls({ zoom: false }).extend([
	new ScaleLine({ units: 'metric' }),
]);


const initMap = (mapRoot: HTMLElement, maxZoom: number): InitMapResult => {
	const vectorLayer = new VectorLayer({
		source: new VectorSource({
			features: [],
		}),
	});
	const sourceCCM = new VectorSource({
		features: [],
	});
	const cnetConstructorLayer = new VectorLayer({
		source: sourceCCM,
		style: CNET_CONSTRUCTOR_VECTOR_LAYER_STYLE,
	});
	const measureSource = new VectorSource();
	const measureVectorLayer = new VectorLayer({
		source: measureSource,
		style: MEASURE_VECTOR_LAYER_STYLE,
	});
	const tileLayer = new TileLayer({
		source: new OSM(),
	});
	const olMap = new OLMap({
		controls: getControls(),
		target: mapRoot,
		layers: [
			tileLayer,
			vectorLayer,
			cnetConstructorLayer,
			measureVectorLayer,
		],
		view: new View({
			zoom: 0,
			maxZoom: maxZoom,
			center: [0, 0],
			constrainResolution: true,
		}),
	});
	return {
		olMap,
		vectorLayer,
		sourceCCM,
		tileLayer,
		measureSource,
	};
};


const fitViewToRoversHistoryPaths = (
	mapInstance: Map | null,
	roversHistoryPathsFeatures: MapFeature[],
	maxZoom: number,
): void => {
	if (mapInstance == null || roversHistoryPathsFeatures.length === 0) {
		return;
	}
	let extent: Extent | null = null;
	roversHistoryPathsFeatures.forEach(feature => {
		const geom = feature.getGeometry();
		if (geom != null) {
			const geomExtent = geom.getExtent();
			extent = extent != null ? extend(extent, geomExtent) : geomExtent.slice();
		}
	});
	if (extent != null) {
		const size = mapInstance.getSize();
		const width = size?.[0];
		const leftPad = width != null ? Math.floor(width / 2) : 50;
		mapInstance.getView().fit(extent, {
			maxZoom: Math.min(maxZoom, 15),
			padding: [50, 50, 50, leftPad],
		});
	}
};


const fitView = (
	mapInstance: Map | null,
	vectorLayer: VectorLayer<VectorSource> | null,
	focusedMountpoint: string,
	baseStationFeatures: MapFeature[],
	mapMode: MapMode,
	roversHistoryPathsFeatures: MapFeature[],
	maxZoom: number,
): void => {
	if (mapInstance == null || vectorLayer == null) {
		return;
	}
	const view = mapInstance.getView();
	if (focusedMountpoint !== '') {
		const features = baseStationFeatures.filter(feature => {
			const typed = asTypedMapFeature(feature);
			if (!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baseStation)) {
				return false;
			}
			return MapHelpers.checkIsFocusedMountpoint(typed.featureData, focusedMountpoint);
		});
		const firstFeature = features[0];
		const firstGeometry = firstFeature?.getGeometry();
		if (firstGeometry != null) {
			view.fit(firstGeometry.getExtent(), {
				maxZoom: Math.min(maxZoom, 15),
			});
			return;
		}
	}
	if (mapMode === MAP_MODE.roversHistory) {
		fitViewToRoversHistoryPaths(mapInstance, roversHistoryPathsFeatures, maxZoom);
		return;
	}
	const source = vectorLayer.getSource();
	if (source != null) {
		view.fit(source.getExtent());
	}
};


export {
	fitView,
	fitViewToRoversHistoryPaths,
	initMap,
};
